import * as React from 'react';
import { Flex } from '../Flex';
import { Icon, type LucideIconName } from '@/hen-ui/atomics/Icon';
import {
  valuePropVariants,
  valuePropChildrenVariants,
} from './valueprop.variants';
import { cn } from '@/hen-ui/utils';

export interface ValuePropProps extends React.PropsWithChildren {
  /** Size variant: "sm" (compact) or "lg". */
  size: 'sm' | 'lg';
  /** Lucide icon name; rendered on the left via Icon atomic. */
  icon: LucideIconName;
}

/**
 * ValueProp — composite for a single value proposition or feature highlight.
 *
 * Renders an Icon on the left and optional content (e.g. Text) on the right
 * inside a bordered card (white background, gray border, rounded corners).
 * All styling uses EDU Design System tokens via valuePropVariants.
 *
 * @example
 * <ValueProp size="lg" icon="check-circle">
 *   <Text variant="b3">Trusted by educators nationwide.</Text>
 * </ValueProp>
 */
export const ValueProp: React.FC<ValuePropProps> = ({
  size,
  icon,
  children,
}) => (
  <Flex
    className={valuePropVariants({ size })}
    direction='row'
    justify='center'
    align='center'
  >
    <Icon icon={icon} size={size} color='secondary' />
    {children && (
      <Flex
        direction='row'
        align='center'
        className={cn('self-stretch grow', valuePropChildrenVariants({ size }))}
      >
        {children}
      </Flex>
    )}
  </Flex>
);
